import { useMemo, useState } from 'react'
import type { Group } from '../types'

export const AUDIENCE_FRIENDS = '__friends__'

/** Both absent = everyone (every group + friends); otherwise the chosen subset. */
export interface AudienceValue {
  audienceGroupIds?: string[]
  audienceFriends?: boolean
}

/**
 * Selection state for an `AudiencePicker`: Friends plus each group as a
 * channel, all selected by default. `initial` restores a saved restricted
 * audience; groups it names that the user has since left are dropped.
 */
export function useAudienceSelection(groups: Group[], initial?: AudienceValue) {
  const channelIds = useMemo(() => [AUDIENCE_FRIENDS, ...groups.map(g => g.id)], [groups])
  const [selected, setSelected] = useState<Set<string>>(() => {
    const restricted = (initial?.audienceGroupIds?.length ?? 0) > 0 || initial?.audienceFriends === true
    if (!restricted) return new Set(channelIds)
    return new Set([...(initial?.audienceFriends ? [AUDIENCE_FRIENDS] : []), ...(initial?.audienceGroupIds ?? [])])
  })
  const chosen = channelIds.filter(id => selected.has(id))
  const isEveryone = chosen.length === channelIds.length
  const value: AudienceValue = isEveryone
    ? {}
    : { audienceGroupIds: chosen.filter(id => id !== AUDIENCE_FRIENDS), audienceFriends: selected.has(AUDIENCE_FRIENDS) }

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectOnly(id: string) {
    setSelected(new Set([id]))
  }

  return { selected, toggle, selectOnly, isEveryone, count: chosen.length, value }
}

export type AudienceSelection = ReturnType<typeof useAudienceSelection>
